// Componente de Perfil do Usuário
import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { User, LogOut, Activity, Calendar, Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';

function UserProfile({ onClose }) {
  const { user, logout } = useAuth();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [currentPasswordValidation, setCurrentPasswordValidation] = useState({
    isValidating: false,
    isValid: null,
    error: ''
  });
  
  const [fieldTouched, setFieldTouched] = useState({
    newPassword: false,
    confirmPassword: false
  });
  
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
  
  const [passwordStrength, setPasswordStrength] = useState({
    hasLower: false,
    hasUpper: false,
    hasNumber: false,
    hasSpecial: false,
    hasLength: false
  });

  const handleLogout = async (logoutAll = false) => {
    setLogoutLoading(true);
    try {
      await logout(logoutAll);
      onClose && onClose();
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      setLogoutLoading(false);
      setShowLogoutConfirm(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    // Validações no frontend
    if (!isPasswordStrong()) {
      setPasswordError('A nova senha deve atender a todos os critérios de segurança');
      setPasswordLoading(false);
      return;
    }

    if (!passwordsMatch) {
      setPasswordError('As senhas não coincidem');
      setPasswordLoading(false);
      return;
    }

    try {
      const API_URL = process.env.NODE_ENV === 'production' 
        ? 'https://dashsimples.vercel.app/api'
        : 'http://localhost:3001/api';

      const token = localStorage.getItem('auth_token');
      
      // Obter token CSRF primeiro
      let csrfToken = null;
      try {
        const csrfResponse = await fetch(`${API_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include'
        });
        
        if (csrfResponse.ok) {
          const csrfData = await csrfResponse.json();
          csrfToken = csrfData.csrfToken;
        }
      } catch (csrfError) {
        console.error('❌ Erro ao obter CSRF token:', csrfError);
      }
      
      const response = await fetch(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...(csrfToken && { 'X-CSRF-Token': csrfToken })
        },
        body: JSON.stringify(passwordData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPasswordSuccess('🎉 Senha alterada com sucesso! Sua conta está mais segura agora.');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        // Reset todos os estados de validação
        setCurrentPasswordValidation({
          isValidating: false,
          isValid: null,
          error: ''
        });
        setFieldTouched({
          newPassword: false,
          confirmPassword: false
        });
        setShowPasswordCriteria(false);
        
        // Usuário controla quando fechar (sem timeout automático)
      } else {
        setPasswordError(data.error || 'Erro ao alterar senha');
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      setPasswordError('Erro de conexão. Tente novamente.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const validateCurrentPassword = async (password) => {
    if (!password || password.length === 0) {
      setCurrentPasswordValidation({
        isValidating: false,
        isValid: null,
        error: ''
      });
      return;
    }

    setCurrentPasswordValidation({
      isValidating: true,
      isValid: null,
      error: ''
    });

    try {
      const API_URL = process.env.NODE_ENV === 'production' 
        ? 'https://dashsimples.vercel.app/api'
        : 'http://localhost:3001/api';

      const token = localStorage.getItem('auth_token');
      
      // Criar uma chamada específica apenas para validar a senha atual
      const response = await fetch(`${API_URL}/auth/validate-current-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword: password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setCurrentPasswordValidation({
          isValidating: false,
          isValid: true,
          error: ''
        });
      } else {
        setCurrentPasswordValidation({
          isValidating: false,
          isValid: false,
          error: data.error || 'Senha atual incorreta'
        });
      }
    } catch (error) {
      console.error('Erro ao validar senha atual:', error);
      setCurrentPasswordValidation({
        isValidating: false,
        isValid: false,
        error: 'Erro de conexão'
      });
    }
  };

  const handleCurrentPasswordBlur = (e) => {
    const password = e.target.value;
    if (password && password.length > 0) {
      validateCurrentPassword(password);
    }
  };

  const checkPasswordStrength = (password) => {
    const strength = {
      hasLower: /[a-z]/.test(password),
      hasUpper: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[@$!%*?&]/.test(password),
      hasLength: password.length >= 8
    };
    setPasswordStrength(strength);
    return strength;
  };

  const isPasswordStrong = () => {
    return Object.values(passwordStrength).every(Boolean);
  };

  const passwordsMatch = passwordData.newPassword && passwordData.confirmPassword && 
                        passwordData.newPassword === passwordData.confirmPassword;

  const handleNewPasswordFocus = () => {
    setShowPasswordCriteria(true);
  };

  const handleNewPasswordBlur = () => {
    setFieldTouched(prev => ({ ...prev, newPassword: true }));
    // Manter critérios visíveis se senha não estiver forte
    if (isPasswordStrong()) {
      setShowPasswordCriteria(false);
    }
  };

  const handleConfirmPasswordBlur = () => {
    setFieldTouched(prev => ({ ...prev, confirmPassword: true }));
  };

  const PasswordCriteria = ({ met, text }) => (
    <div className={`flex items-center text-xs ${met ? 'text-green-600' : 'text-gray-400'}`}>
      <CheckCircle className={`w-3 h-3 mr-1 ${met ? 'text-green-500' : 'text-gray-300'}`} />
      {text}
    </div>
  );

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="w-full max-w-md mx-auto bg-white rounded-lg shadow-lg p-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <User className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          {user?.name || 'Usuário'}
        </h2>
        <p className="text-gray-600">{user?.email}</p>
      </div>

      {/* Informações da conta */}
      <div className="space-y-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            Informações da Conta
          </h3>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Membro desde:</span>
              <span className="text-gray-900">{formatDate(user?.created_at)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Último acesso:</span>
              <span className="text-gray-900">{formatDateTime(user?.last_login)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Ações */}
      <div className="space-y-3">
        {/* Botão Alterar Senha */}
        {!showChangePassword && !showLogoutConfirm && (
          <button
            onClick={() => setShowChangePassword(true)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center"
          >
            <Lock className="w-4 h-4 mr-2" />
            Alterar Senha
          </button>
        )}

        {/* Formulário de Alterar Senha */}
        {showChangePassword && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-3">Alterar Senha</h4>
            
            <form onSubmit={handleChangePassword} className="space-y-3">
              {/* Senha Atual */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Senha Atual
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) => {
                      setPasswordData(prev => ({
                        ...prev,
                        currentPassword: e.target.value
                      }));
                      // Limpar validação quando o usuário digitar
                      if (currentPasswordValidation.isValid !== null) {
                        setCurrentPasswordValidation({
                          isValidating: false,
                          isValid: null,
                          error: ''
                        });
                      }
                    }}
                    onBlur={handleCurrentPasswordBlur}
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 pr-10 ${
                      currentPasswordValidation.isValid === true
                        ? 'border-green-300 focus:ring-green-500 focus:border-transparent'
                        : currentPasswordValidation.isValid === false
                        ? 'border-red-300 focus:ring-red-500 focus:border-transparent'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-transparent'
                    }`}
                    placeholder="Digite sua senha atual"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-1">
                    {/* Indicador de validação */}
                    {currentPasswordValidation.isValidating && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    )}
                    {currentPasswordValidation.isValid === true && (
                      <div className="w-4 h-4 text-green-500">✓</div>
                    )}
                    {currentPasswordValidation.isValid === false && (
                      <div className="w-4 h-4 text-red-500">✗</div>
                    )}
                    {/* Botão de visibilidade */}
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('current')}
                      className="ml-1"
                    >
                      {showPasswords.current ? (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
                {/* Mensagem de validação da senha atual */}
                {currentPasswordValidation.error && (
                  <div className="mt-1 text-xs text-red-600">
                    {currentPasswordValidation.error}
                  </div>
                )}
                {currentPasswordValidation.isValid === true && (
                  <div className="mt-1 text-xs text-green-600">
                    <span className="text-xs">Senha atual válida ✓</span>
                  </div>
                )}
              </div>

              {/* Nova Senha */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => {
                      const newPassword = e.target.value;
                      setPasswordData(prev => ({
                        ...prev,
                        newPassword: newPassword
                      }));
                      // Verificar força da senha
                      checkPasswordStrength(newPassword);
                    }}
                    onFocus={handleNewPasswordFocus}
                    onBlur={handleNewPasswordBlur}
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:border-transparent pr-10 ${
                      isPasswordStrong()
                        ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                        : fieldTouched.newPassword && !isPasswordStrong() && passwordData.newPassword
                        ? 'border-orange-300 focus:ring-orange-500 focus:border-orange-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    placeholder="Digite sua nova senha"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showPasswords.new ? (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>

                {/* Critérios de senha - só mostrar quando em foco ou quando não está forte */}
                {(showPasswordCriteria || (fieldTouched.newPassword && !isPasswordStrong())) && passwordData.newPassword && (
                  <div className="mt-2 space-y-1">
                    <PasswordCriteria met={passwordStrength.hasLength} text="Pelo menos 8 caracteres" />
                    <PasswordCriteria met={passwordStrength.hasLower} text="Uma letra minúscula" />
                    <PasswordCriteria met={passwordStrength.hasUpper} text="Uma letra maiúscula" />
                    <PasswordCriteria met={passwordStrength.hasNumber} text="Um número" />
                    <PasswordCriteria met={passwordStrength.hasSpecial} text="Um caractere especial (@$!%*?&)" />
                  </div>
                )}

                {/* Mensagem de sucesso quando senha está forte */}
                {isPasswordStrong() && passwordData.newPassword && (
                  <div className="mt-2 flex items-center text-green-600">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    <span className="text-xs">Senha forte ✓</span>
                  </div>
                )}
              </div>

              {/* Confirmar Nova Senha */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Confirmar Nova Senha
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({
                      ...prev,
                      confirmPassword: e.target.value
                    }))}
                    onBlur={handleConfirmPasswordBlur}
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:border-transparent pr-10 ${
                      passwordData.confirmPassword && passwordsMatch
                        ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                        : fieldTouched.confirmPassword && passwordData.confirmPassword && !passwordsMatch
                        ? 'border-orange-300 focus:ring-orange-500 focus:border-orange-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    placeholder="Confirme sua nova senha"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showPasswords.confirm ? (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>

                {/* Feedback para confirmação de senha */}
                {fieldTouched.confirmPassword && passwordData.confirmPassword && !passwordsMatch && (
                  <div className="mt-2 flex items-center text-orange-600">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    <span className="text-xs">As senhas não coincidem</span>
                  </div>
                )}
                {passwordData.confirmPassword && passwordsMatch && (
                  <div className="mt-2 flex items-center text-green-600">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    <span className="text-xs">Senhas coincidem ✓</span>
                  </div>
                )}
              </div>

              {/* Mensagens de erro/sucesso */}
              {passwordError && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                  {passwordError}
                </div>
              )}
              
              {passwordSuccess && (
                <div className="text-xs text-green-600 bg-green-50 border border-green-200 rounded-md p-3">
                  <div className="flex items-center justify-between">
                    <span className="flex-1">{passwordSuccess}</span>
                    <button
                      onClick={() => {
                        setPasswordSuccess('');
                        setShowChangePassword(false);
                      }}
                      className="ml-3 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md transition-colors duration-200"
                    >
                      Fechar
                    </button>
                  </div>
                </div>
              )}

              {/* Botões */}
              <div className="flex space-x-2 pt-2">
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-medium py-2 px-3 rounded-md transition-colors duration-200 flex items-center justify-center"
                >
                  {passwordLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    'Alterar'
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePassword(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                    setPasswordError('');
                    setPasswordSuccess('');
                    setCurrentPasswordValidation({
                      isValidating: false,
                      isValid: null,
                      error: ''
                    });
                    setFieldTouched({
                      newPassword: false,
                      confirmPassword: false
                    });
                    setShowPasswordCriteria(false);
                    setPasswordStrength({
                      hasLower: false,
                      hasUpper: false,
                      hasNumber: false,
                      hasSpecial: false,
                      hasLength: false
                    });
                  }}
                  disabled={passwordLoading}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 text-sm font-medium py-2 px-3 rounded-md transition-colors duration-200"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Botão de Logout */}
        {!showChangePassword && !showLogoutConfirm && (
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </button>
        )}

        {/* Confirmação de Logout */}
        {showLogoutConfirm && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm font-medium text-red-900 mb-4">
              Tem certeza que deseja sair?
            </p>
            <div className="flex space-x-2">
              <button
                onClick={handleLogout}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-3 rounded-md transition-colors duration-200"
              >
                Sim, Sair
              </button>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 text-sm font-medium py-2 px-3 rounded-md transition-colors duration-200"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="text-xs text-gray-500 text-center">
          <p>🔒 Sua privacidade é nossa prioridade</p>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
